import type { OpenArenaVoteWithConfigNames } from '@meridian-ssa/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { EvalLayout } from '@/components/eval/EvalLayout';
import { ExecuteSeedModal } from '@/components/eval/ExecuteSeedModal';
import { ExecutionDetailModal } from '@/components/eval/ExecutionDetailModal';
import { VoteComparisonModal } from '@/components/eval/VoteComparisonModal';
import { BattleSelectionCheckbox } from '@/components/SelectionPanel';
import { SpreadJSViewer } from '@/components/SpreadJSViewer';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SourceBadge } from '@/components/ui/SourceBadge';
import { type BadgeVariant, StatusBadge } from '@/components/ui/StatusBadge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui/Table';
import { apiRequest } from '@/config';
import { logError } from '@/lib/logger';

// Types
interface ModelStats {
  config_id: string;
  config_name: string;
  wins: number;
  losses: number;
  ties: number;
  both_bad: number;
  total_matches: number;
  elo: number;
}

interface ArenaResult {
  id: string;
  model_config_id: string;
  config_name?: string;
  status: string;
  is_valid_json: boolean;
  evaluation_ok: boolean;
  formula_errors: boolean;
  execution_time_ms: number;
  token_usage?: any;
  engine_checks?: any;
  error?: string;
  sheet_json?: any;
  compute_values?: any;
}

interface ArenaRun {
  id: string;
  prompt_id: string | null;
  prompt_text: string;
  category: string | null;
  status: string;
  source: 'seed' | 'arena';
  total_models: number;
  completed_models: number;
  created_at: string;
}

interface VoterDetail {
  userEmail: string;
  voteCount: number;
  lastVotedAt: string;
}

// Utility functions
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatEmail(email: string): string {
  return email.split('@')[0];
}

function getStatusVariant(status: string): BadgeVariant {
  const variants: Record<string, BadgeVariant> = {
    completed: 'success',
    running: 'info',
    pending: 'warning',
    failed: 'error',
  };
  return variants[status] || 'neutral';
}

// Sub-components

function StatsCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number | string;
  color?: string;
}) {
  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #E8E0D8',
        borderRadius: '12px',
        padding: '1.5rem',
      }}
    >
      <div
        style={{
          fontSize: '0.8125rem',
          color: '#6B6B6B',
          marginBottom: '0.5rem',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '1.5rem',
          fontWeight: 600,
          color: color || '#1A1A1A',
        }}
      >
        {value}
      </div>
    </div>
  );
}

function ResultStatusIcon({ result }: { result: ArenaResult }) {
  if (result.status === 'pending') {
    return <span title="Pending">⏳</span>;
  }
  if (result.status === 'running') {
    return <span title="Running">🔄</span>;
  }
  if (result.status === 'failed' || result.error) {
    return <span title="Failed">❌</span>;
  }
  if (!result.is_valid_json) {
    return <span title="Invalid JSON">❌</span>;
  }
  if (result.formula_errors) {
    return <span title="Formula Errors">⚠️</span>;
  }
  if (result.evaluation_ok) {
    return <span title="Success">✅</span>;
  }
  return <span title="Unknown">❓</span>;
}

function VotingActivitySection({ voters }: { voters: VoterDetail[] }) {
  if (voters.length === 0) return null;

  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #E8E0D8',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '2rem',
      }}
    >
      <h2
        style={{
          fontSize: '1.125rem',
          fontWeight: 600,
          color: '#1A1A1A',
          margin: 0,
          marginBottom: '1rem',
        }}
      >
        Voting Activity
      </h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
        {voters.map(voter => (
          <div
            key={voter.userEmail}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              borderRadius: '8px',
              background: '#F5F0EB',
              border: '1px solid #E8E0D8',
            }}
          >
            <div>
              <div
                style={{
                  fontWeight: 500,
                  fontSize: '0.9375rem',
                  color: '#1A1A1A',
                }}
              >
                {formatEmail(voter.userEmail)}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6B6B6B' }}>
                {voter.voteCount} votes • {formatDate(voter.lastVotedAt)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ModelRankingsSection({ stats }: { stats: ModelStats[] }) {
  if (stats.length === 0) return null;

  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #E8E0D8',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '2rem',
      }}
    >
      <h2
        style={{
          fontSize: '1.125rem',
          fontWeight: 600,
          color: '#1A1A1A',
          margin: 0,
          marginBottom: '1rem',
        }}
      >
        Model Rankings
      </h2>
      <Table>
        <TableHead>
          <TableRow hover={false}>
            <TableHeaderCell width="60px">Rank</TableHeaderCell>
            <TableHeaderCell>Model</TableHeaderCell>
            <TableHeaderCell align="center" width="80px">
              Elo
            </TableHeaderCell>
            <TableHeaderCell align="center" width="120px">
              W/L/T
            </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {stats.map((model, idx) => (
            <TableRow
              key={model.config_id}
              style={{
                background: idx === 0 ? '#FEFCE8' : undefined,
              }}
            >
              <TableCell>
                <span
                  style={{
                    fontWeight: 600,
                    color: idx === 0 ? '#CA8A04' : '#6B6B6B',
                  }}
                >
                  #{idx + 1}
                </span>
              </TableCell>
              <TableCell>
                <span style={{ fontWeight: 500, color: '#1A1A1A' }}>
                  {model.config_name}
                </span>
              </TableCell>
              <TableCell align="center">
                <span style={{ fontWeight: 600 }}>{Math.round(model.elo)}</span>
              </TableCell>
              <TableCell align="center">
                <span style={{ color: '#2E7D4A' }}>{model.wins}</span>
                {' / '}
                <span style={{ color: '#C41E3A' }}>{model.losses}</span>
                {' / '}
                <span style={{ color: '#6B6B6B' }}>{model.ties}</span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

interface VotesSectionProps {
  votes: OpenArenaVoteWithConfigNames[];
  results: ArenaResult[];
  run: ArenaRun | null;
  onViewVote: (vote: OpenArenaVoteWithConfigNames) => void;
}

function VotesSection({ votes, results, run, onViewVote }: VotesSectionProps) {
  const [modelFilter, setModelFilter] = useState<string>('all');
  const [outcomeFilter, setOutcomeFilter] = useState<string>('all');

  // Create a lookup map for results by ID
  const resultsById = useMemo(() => {
    const map = new Map<string, ArenaResult>();
    for (const r of results) {
      map.set(r.id, r);
    }
    return map;
  }, [results]);

  // Helper to get full selection data for a vote
  const getSelectionData = useCallback(
    (vote: OpenArenaVoteWithConfigNames) => {
      const resultA = resultsById.get(vote.result_a_id);
      const resultB = resultsById.get(vote.result_b_id);

      return {
        id: vote.id,
        arenaRunId: vote.arena_run_id,
        promptText: run?.prompt_text || '',
        promptId: run?.prompt_id || null,
        category: run?.category || null,
        source: run?.source || null,
        resultA: {
          id: vote.result_a_id,
          configId: vote.config_a_id,
          configName: vote.config_a_name,
          sheetJson: resultA?.sheet_json || null,
        },
        resultB: {
          id: vote.result_b_id,
          configId: vote.config_b_id,
          configName: vote.config_b_name,
          sheetJson: resultB?.sheet_json || null,
        },
        winner: vote.choice,
      };
    },
    [resultsById, run]
  );

  if (votes.length === 0) return null;

  // Get unique models from votes
  const uniqueModels = Array.from(
    new Set(
      votes.flatMap(v => [v.config_a_name, v.config_b_name]).filter(Boolean)
    )
  ).sort();

  const filteredVotes = votes
    .filter(vote => {
      if (modelFilter === 'all') return true;
      return (
        vote.config_a_name === modelFilter || vote.config_b_name === modelFilter
      );
    })
    .filter(vote => {
      if (outcomeFilter === 'all' || modelFilter === 'all') return true;
      if (outcomeFilter === 'ties') return vote.choice === 'tie';

      const modelWon =
        (vote.choice === 'a' && vote.config_a_name === modelFilter) ||
        (vote.choice === 'b' && vote.config_b_name === modelFilter);
      return outcomeFilter === 'wins' ? modelWon : !modelWon;
    });

  const getWinnerDisplay = (vote: OpenArenaVoteWithConfigNames) => {
    switch (vote.choice) {
      case 'a':
        return { winner: vote.config_a_name, loser: vote.config_b_name };
      case 'b':
        return { winner: vote.config_b_name, loser: vote.config_a_name };
      case 'tie':
        return { winner: null, loser: null, tie: true };
      case 'both_bad':
        return { winner: null, loser: null, bothBad: true };
      default:
        return { winner: null, loser: null };
    }
  };

  const formatVoteDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #E8E0D8',
        borderRadius: '12px',
        overflow: 'hidden',
        marginBottom: '2rem',
      }}
    >
      <div
        style={{
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #E8E0D8',
          background: '#F5F0EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '1rem',
          flexWrap: 'wrap',
        }}
      >
        <h2
          style={{
            fontSize: '1.125rem',
            fontWeight: 600,
            color: '#1A1A1A',
            margin: 0,
          }}
        >
          Individual Votes ({filteredVotes.length}
          {filteredVotes.length !== votes.length && ` of ${votes.length}`})
        </h2>

        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <select
            value={modelFilter}
            onChange={e => setModelFilter(e.target.value)}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #E8E0D8',
              borderRadius: '6px',
              background: modelFilter !== 'all' ? '#8B2332' : 'white',
              color: modelFilter !== 'all' ? 'white' : '#1A1A1A',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: modelFilter !== 'all' ? 600 : 400,
            }}
          >
            <option value="all">All Models</option>
            {uniqueModels.map(model => (
              <option key={model} value={model}>
                {model}
              </option>
            ))}
          </select>

          {modelFilter !== 'all' && (
            <select
              value={outcomeFilter}
              onChange={e => setOutcomeFilter(e.target.value)}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid #E8E0D8',
                borderRadius: '6px',
                background: outcomeFilter !== 'all' ? '#8B2332' : 'white',
                color: outcomeFilter !== 'all' ? 'white' : '#1A1A1A',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: outcomeFilter !== 'all' ? 600 : 400,
              }}
            >
              <option value="all">All Outcomes</option>
              <option value="wins">Wins Only</option>
              <option value="losses">Losses Only</option>
              <option value="ties">Ties Only</option>
            </select>
          )}
        </div>
      </div>

      <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow hover={false}>
              <TableHeaderCell align="center" width="50px">
                Select
              </TableHeaderCell>
              <TableHeaderCell>Model A</TableHeaderCell>
              <TableHeaderCell align="center" width="80px">
                vs
              </TableHeaderCell>
              <TableHeaderCell>Model B</TableHeaderCell>
              <TableHeaderCell align="center" width="140px">
                Result
              </TableHeaderCell>
              <TableHeaderCell width="120px">Voter</TableHeaderCell>
              <TableHeaderCell align="right" width="130px">
                Date
              </TableHeaderCell>
              <TableHeaderCell align="center" width="80px">
                View
              </TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredVotes.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  style={{
                    textAlign: 'center',
                    padding: '2rem',
                    color: '#6B6B6B',
                  }}
                >
                  No votes match the current filters.
                </td>
              </tr>
            ) : null}
            {filteredVotes.map(vote => {
              const result = getWinnerDisplay(vote);
              const isAWinner = vote.choice === 'a';
              const isBWinner = vote.choice === 'b';
              const selectionData = getSelectionData(vote);

              return (
                <TableRow key={vote.id}>
                  <TableCell align="center">
                    <BattleSelectionCheckbox battle={selectionData} />
                  </TableCell>
                  <TableCell>
                    <span
                      style={{
                        fontWeight: isAWinner ? 600 : 400,
                        color: isAWinner ? '#2E7D4A' : '#1A1A1A',
                      }}
                    >
                      {vote.config_a_name}
                      {isAWinner && ' 🏆'}
                    </span>
                  </TableCell>
                  <TableCell align="center">
                    <span style={{ color: '#6B6B6B', fontSize: '0.875rem' }}>
                      vs
                    </span>
                  </TableCell>
                  <TableCell>
                    <span
                      style={{
                        fontWeight: isBWinner ? 600 : 400,
                        color: isBWinner ? '#2E7D4A' : '#1A1A1A',
                      }}
                    >
                      {vote.config_b_name}
                      {isBWinner && ' 🏆'}
                    </span>
                  </TableCell>
                  <TableCell align="center">
                    {result.tie ? (
                      <span
                        style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '0.8125rem',
                          background: '#F5F0EB',
                          color: '#6B6B6B',
                        }}
                      >
                        Tie
                      </span>
                    ) : result.bothBad ? (
                      <span
                        style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '0.8125rem',
                          background: '#FEF2F2',
                          color: '#C41E3A',
                        }}
                      >
                        Both Bad
                      </span>
                    ) : (
                      <span
                        style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '0.8125rem',
                          background: '#ECFDF5',
                          color: '#2E7D4A',
                          fontWeight: 500,
                        }}
                      >
                        {result.winner} wins
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span style={{ fontSize: '0.8125rem', color: '#6B6B6B' }}>
                      {vote.user_email
                        ? vote.user_email.split('@')[0]
                        : 'Anonymous'}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span style={{ fontSize: '0.8125rem', color: '#6B6B6B' }}>
                      {formatVoteDate(vote.created_at)}
                    </span>
                  </TableCell>
                  <TableCell align="center">
                    <button
                      onClick={() => onViewVote(vote)}
                      style={{
                        background: 'none',
                        border: '1px solid #E8E0D8',
                        borderRadius: '6px',
                        padding: '4px 10px',
                        fontSize: '0.8125rem',
                        color: '#8B2332',
                        cursor: 'pointer',
                        fontWeight: 500,
                      }}
                    >
                      Compare
                    </button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

interface ResultsGridProps {
  title: string;
  results: ArenaResult[];
  variant: 'valid' | 'invalid';
  onResultClick: (resultId: string) => void;
}

function ResultsGrid({
  title,
  results,
  variant,
  onResultClick,
}: ResultsGridProps) {
  if (results.length === 0) return null;

  const isValid = variant === 'valid';
  const borderColor = isValid ? '#D4E5D0' : '#FECACA';
  const bgColor = isValid ? '#FAFDF8' : '#FEF2F2';
  const hoverBorder = isValid ? '#2E7D4A' : '#C41E3A';
  const titleColor = isValid ? '#2E7D4A' : '#C41E3A';

  return (
    <div style={{ marginBottom: '1rem' }}>
      <h3
        style={{
          fontSize: '0.875rem',
          fontWeight: 600,
          color: titleColor,
          marginBottom: '1rem',
        }}
      >
        {title} ({results.length})
      </h3>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '1rem',
        }}
      >
        {results.map(result => (
          <div
            key={result.id}
            onClick={() => onResultClick(result.id)}
            style={{
              padding: '1rem',
              border: `1px solid ${borderColor}`,
              borderRadius: '8px',
              background: bgColor,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = hoverBorder;
              if (isValid) {
                e.currentTarget.style.boxShadow =
                  '0 2px 8px rgba(46, 125, 74, 0.1)';
              }
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = borderColor;
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '0.5rem',
              }}
            >
              <span style={{ fontWeight: 500, color: '#1A1A1A' }}>
                {result.config_name || result.model_config_id}
              </span>
              <ResultStatusIcon result={result} />
            </div>
            <div
              style={{
                fontSize: '0.75rem',
                color: isValid ? '#6B6B6B' : '#C41E3A',
              }}
            >
              {isValid
                ? result.execution_time_ms
                  ? `${(result.execution_time_ms / 1000).toFixed(1)}s`
                  : '—'
                : result.error ||
                  (result.status !== 'completed'
                    ? result.status
                    : 'Validation failed')}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WinnerPreviewSection({
  stats,
  validResults,
}: {
  stats: ModelStats[];
  validResults: ArenaResult[];
}) {
  if (validResults.length === 0 || stats.length === 0) return null;

  const winnerResult = validResults.find(
    r => r.model_config_id === stats[0]?.config_id
  );

  if (!winnerResult) {
    return null;
  }

  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #E8E0D8',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #E8E0D8',
          background: '#FEFCE8',
        }}
      >
        <h2
          style={{
            fontSize: '1.125rem',
            fontWeight: 600,
            color: '#1A1A1A',
            margin: 0,
          }}
        >
          🏆 Current Leader: {stats[0]?.config_name}
        </h2>
      </div>
      <div style={{ padding: '1.5rem' }}>
        {winnerResult.sheet_json?.sheets?.length > 0 ? (
          <SpreadJSViewer
            sheetJson={winnerResult.sheet_json}
            computedValues={winnerResult.compute_values}
            height={400}
          />
        ) : (
          <div
            style={{
              textAlign: 'center',
              padding: '2rem',
              color: '#6B6B6B',
              background: '#FEF2F2',
              borderRadius: '8px',
            }}
          >
            <p style={{ margin: 0, fontWeight: 500 }}>
              No spreadsheet data available
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Main Component
export default function UnifiedPromptDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [run, setRun] = useState<ArenaRun | null>(null);
  const [results, setResults] = useState<ArenaResult[]>([]);
  const [stats, setStats] = useState<ModelStats[]>([]);
  const [votes, setVotes] = useState<OpenArenaVoteWithConfigNames[]>([]);
  const [totalVotes, setTotalVotes] = useState(0);
  const [voters, setVoters] = useState<VoterDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedResultId, setSelectedResultId] = useState<string | null>(null);
  const [selectedResultFull, setSelectedResultFull] =
    useState<ArenaResult | null>(null);
  const [loadingSelectedResult, setLoadingSelectedResult] = useState(false);
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [selectedVote, setSelectedVote] =
    useState<OpenArenaVoteWithConfigNames | null>(null);

  // Fetch full result when a result is selected
  useEffect(() => {
    if (!selectedResultId) {
      setSelectedResultFull(null);
      return;
    }

    async function fetchFullResult() {
      try {
        setLoadingSelectedResult(true);
        const response = await apiRequest(
          `/api/arena/results/${selectedResultId}`
        );
        setSelectedResultFull(response.result);
      } catch (err) {
        logError('Failed to fetch full result', err);
        alert('Failed to load result details');
        setSelectedResultId(null);
      } finally {
        setLoadingSelectedResult(false);
      }
    }

    fetchFullResult();
  }, [selectedResultId]);

  const loadData = async () => {
    if (!id) return;

    try {
      setLoading(true);

      const [runData, resultsData, statsData, votesData, promptsDetailedData] =
        await Promise.all([
          apiRequest(`/api/arena/runs/${id}`),
          apiRequest(`/api/arena/runs/${id}/results`),
          apiRequest(`/api/arena/runs/${id}/stats`).catch(() => null),
          apiRequest(`/api/arena/runs/${id}/battles`).catch(() => null),
          apiRequest('/api/leaderboard/unified-prompts?limit=1000'),
        ]);

      setRun(runData.run);
      setResults(resultsData.results || []);

      if (statsData) {
        setStats(statsData.stats || []);
        setTotalVotes(statsData.total_votes || 0);
      }

      if (votesData) {
        setVotes(votesData.battles || []);
      }

      const prompts =
        promptsDetailedData.prompts || promptsDetailedData.seedPrompts || [];
      const thisPrompt = prompts.find((p: any) => p.id === id);
      if (thisPrompt) {
        setVoters(thisPrompt.voters || []);
      }
    } catch (err: any) {
      logError('Failed to load prompt details', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  // Loading state
  if (loading) {
    return (
      <EvalLayout>
        <div
          style={{
            textAlign: 'center',
            padding: '4rem',
            color: '#6B6B6B',
          }}
        >
          Loading...
        </div>
      </EvalLayout>
    );
  }

  // Error state
  if (error || !run) {
    return (
      <EvalLayout>
        <EmptyState
          title={error || 'Prompt not found'}
          description="The requested prompt could not be loaded."
          action={
            <Link
              to="/eval/unified"
              style={{
                display: 'inline-block',
                padding: '0.75rem 1.5rem',
                background: '#8B2332',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: 500,
              }}
            >
              Back to Prompts
            </Link>
          }
        />
      </EvalLayout>
    );
  }

  const resultIsValid = (result: ArenaResult) =>
    result.is_valid_json && result.status === 'completed';

  const validResults = results.filter(r => resultIsValid(r));
  const invalidResults = results.filter(r => !resultIsValid(r));

  return (
    <EvalLayout>
      {/* Breadcrumb */}
      <div style={{ marginBottom: '1.5rem' }}>
        <Link
          to="/eval/unified"
          style={{
            fontSize: '0.875rem',
            color: '#8B2332',
            textDecoration: 'none',
          }}
        >
          ← Unified Prompts
        </Link>
      </div>

      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '1rem',
            marginBottom: '1rem',
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '0.75rem',
              }}
            >
              <SourceBadge source={run.source} />
              <StatusBadge variant={getStatusVariant(run.status)}>
                {run.status}
              </StatusBadge>
              {run.category && (
                <span
                  style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '0.8125rem',
                    background: '#F5F0EB',
                    color: '#6B6B6B',
                  }}
                >
                  {run.category}
                </span>
              )}
            </div>
            <h1
              style={{
                fontSize: '1.5rem',
                fontWeight: 600,
                color: '#1A1A1A',
                margin: 0,
                marginBottom: '0.5rem',
                lineHeight: 1.4,
              }}
            >
              {run.prompt_text}
            </h1>
            <p
              style={{
                fontSize: '0.8125rem',
                color: '#6B6B6B',
                margin: 0,
              }}
            >
              Created {formatDate(run.created_at)}
            </p>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', flexShrink: 0 }}>
            <Button
              variant="secondary"
              onClick={() => setShowExecuteModal(true)}
            >
              Run Models
            </Button>
            {validResults.length >= 3 && (
              <Link
                to={`/arena/${id}?source=${run.source}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '0.75rem 1.5rem',
                  background: '#8B2332',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                }}
              >
                Vote on This Prompt →
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem',
        }}
      >
        <StatsCard label="Total Votes" value={totalVotes} />
        <StatsCard
          label="Valid Results"
          value={validResults.length}
          color="#2E7D4A"
        />
        <StatsCard label="Total Results" value={results.length} />
        <StatsCard label="Voters" value={voters.length} />
      </div>

      {/* Voting Activity */}
      <VotingActivitySection voters={voters} />

      {/* Model Rankings */}
      <ModelRankingsSection stats={stats} />

      {/* Individual Votes */}
      <VotesSection
        votes={votes}
        results={results}
        run={run}
        onViewVote={setSelectedVote}
      />

      {/* Results Section */}
      <div
        style={{
          background: '#FFFFFF',
          border: '1px solid #E8E0D8',
          borderRadius: '12px',
          overflow: 'hidden',
          marginBottom: '2rem',
        }}
      >
        <div
          style={{
            padding: '1rem 1.5rem',
            borderBottom: '1px solid #E8E0D8',
            background: '#F5F0EB',
          }}
        >
          <h2
            style={{
              fontSize: '1.125rem',
              fontWeight: 600,
              color: '#1A1A1A',
              margin: 0,
            }}
          >
            Model Results ({results.length})
          </h2>
        </div>

        <div style={{ padding: '1.5rem' }}>
          <ResultsGrid
            title="Valid Results"
            results={validResults}
            variant="valid"
            onResultClick={setSelectedResultId}
          />

          {invalidResults.length > 0 && validResults.length > 0 && (
            <div
              style={{
                height: '1px',
                background: '#E8E0D8',
                margin: '1.5rem 0',
              }}
            />
          )}

          <ResultsGrid
            title="Invalid/Failed Results"
            results={invalidResults}
            variant="invalid"
            onResultClick={setSelectedResultId}
          />

          {results.length === 0 && (
            <div
              style={{
                textAlign: 'center',
                padding: '2rem',
                color: '#6B6B6B',
              }}
            >
              No results yet. Click "Run Models" to execute models on this
              prompt.
            </div>
          )}
        </div>
      </div>

      {/* Winner Preview */}
      <WinnerPreviewSection stats={stats} validResults={validResults} />

      {/* Execution Detail Modal */}
      {(selectedResultId || selectedResultFull) && (
        <ExecutionDetailModal
          result={
            (selectedResultFull ||
              results.find(r => r.id === selectedResultId) || {
                id: selectedResultId,
              }) as any
          }
          onClose={() => setSelectedResultId(null)}
          loading={loadingSelectedResult}
        />
      )}

      {/* Execute Seed Modal */}
      {showExecuteModal && run && (
        <ExecuteSeedModal
          runId={run.id}
          promptText={run.prompt_text}
          existingConfigIds={results.map(r => r.model_config_id)}
          onClose={() => setShowExecuteModal(false)}
          onExecutionStarted={() => {
            setTimeout(() => loadData(), 1000);
          }}
        />
      )}

      {/* Vote Comparison Modal */}
      {selectedVote && (
        <VoteComparisonModal
          vote={selectedVote}
          run={run}
          onClose={() => setSelectedVote(null)}
        />
      )}
    </EvalLayout>
  );
}
